import React, { useState } from "react";
import { CButton, CFormInput } from "@coreui/react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { SectionCard } from "../__sections";
import { colors } from "../../../config/theme";
import PlusIcon from "../../../components/icons/PlusIcon";
import { MapPin, X, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { ACTIVITY_CATEGORIES, emptyDraft } from "../../../helpers/constants";
import { StepDraft, WizardStage } from "../../../helpers/types";
import { normalizeItinerary, toDraft } from "../../../helpers/general";
import LocationPicker from "../../../components/LocationPicker";

export default function Itineraries() {
  const { control, watch } = useFormContext();
  const {
    fields: itineraryFields,
    append: appendItinerary,
    remove: removeItinerary,
    update: updateItinerary,
    move: moveItinerary,
  } = useFieldArray({ control, name: "itineraries" });

  const pickupDropoff = watch("pickupDropoff");

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const openNewStep = () => {
    setEditingIndex(null);
    setWizardOpen(true);
  };

  const openEditStep = (index: number) => {
    setEditingIndex(index);
    setWizardOpen(true);
  };

  const resyncOrders = (list: typeof itineraryFields) => {
    list.forEach((item: any, idx) => {
      if (item.order !== idx + 1) {
        updateItinerary(idx, { ...item, order: idx + 1 });
      }
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    moveItinerary(index, index - 1);
    setTimeout(() => resyncOrders(control._formValues.itineraries), 0);
  };

  const handleMoveDown = (index: number) => {
    if (index === itineraryFields.length - 1) return;
    moveItinerary(index, index + 1);
    setTimeout(() => resyncOrders(control._formValues.itineraries), 0);
  };

  const handleDelete = (index: number) => {
    removeItinerary(index);
    setTimeout(() => resyncOrders(control._formValues.itineraries), 0);
  };

  const handleWizardSave = (draft: StepDraft) => {
    const duration =
      draft.durationHours || draft.durationMinutes
        ? `${draft.durationHours ? `${draft.durationHours}h` : ""}${
            draft.durationMinutes ? ` ${draft.durationMinutes}min` : ""
          }`.trim()
        : undefined;

    const description =
      draft.type === "activity"
        ? draft.selectedActivities.join(", ")
        : draft.transferMode;

    const title =
      draft.title?.trim() ||
      draft.placeName?.trim() ||
      draft.address?.trim() ||
      (draft.type === "activity"
        ? draft.selectedActivities.filter(Boolean).join(", ")
        : draft.transferMode.trim());

    const location =
      draft.address || draft.latitude || draft.longitude
        ? {
            address: draft.address || undefined,
            name: draft.placeName || undefined,
            latitude: draft.latitude,
            longitude: draft.longitude,
            city: draft.city || undefined,
            placeId: draft.placeId || undefined,
          }
        : undefined;

    const payload = normalizeItinerary(
      {
        type: draft.type,
        description,
        title,
        activityType:
          draft.type === "activity"
            ? draft.selectedActivities.join(",")
            : undefined,
        transportMode:
          draft.type === "transfer" ? draft.transferMode : undefined,
        duration,
        isOptional: draft.isOptional,
        location,
        order:
          editingIndex !== null
            ? (itineraryFields[editingIndex] as any).order
            : itineraryFields.length + 1,
      },
      editingIndex !== null ? editingIndex : itineraryFields.length,
    );

    if (editingIndex !== null) updateItinerary(editingIndex, payload);
    else appendItinerary(payload);

    setWizardOpen(false);
    setEditingIndex(null);
  };

  return (
    <div className="animate-fade-in">
      <SectionCard
        title="Étapes de l'itinéraire"
        subtitle="Décrivez le déroulé de l'activité, dans l'ordre chronologique."
      >
        <div className="position-relative" style={{ paddingLeft: 8 }}>
          <div
            className="position-absolute"
            style={{
              left: 19,
              top: 24,
              bottom: 24,
              width: 0,
              borderLeft: "2px dotted #f5a623",
            }}
          />

          <TimelineNode
            filled
            icon={<span style={{ fontWeight: 700 }}>V</span>}
            subtitle={
              pickupDropoff?.isPickupAvailable
                ? pickupDropoff?.pickupDescription || "Défini à l'étape 2"
                : "Non défini"
            }
            type="pickup"
          />

          {itineraryFields.map((field: any, index) => (
            <TimelineNode
              key={field.id}
              icon={
                field.type === "transfer" ? (
                  <span style={{ fontSize: 11, fontWeight: 700 }}>
                    {field.order}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 700 }}>
                    {field.order}
                  </span>
                )
              }
              address={
                field.type === "activity"
                  ? field.location?.name ||
                    field.location?.address ||
                    field.title
                  : field.title
              }
              subtitle={
                field.type === "activity"
                  ? [
                      field.description,
                      field.duration ? `(${field.duration})` : null,
                      field.isOptional ? "Facultatif" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : [
                      field.duration ? `(${field.duration})` : null,
                      field.isOptional ? "Facultatif" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
              }
              actions={
                <StepActions
                  onEdit={() => openEditStep(index)}
                  onMoveUp={index > 0 ? () => handleMoveUp(index) : undefined}
                  onMoveDown={
                    index < itineraryFields.length - 1
                      ? () => handleMoveDown(index)
                      : undefined
                  }
                  onDelete={() => handleDelete(index)}
                />
              }
              type={field.type}
            />
          ))}

          <TimelineNode
            filled
            isLast
            subtitle={
              pickupDropoff?.isDropoffAvailable
                ? pickupDropoff?.dropoffDescription ||
                  pickupDropoff?.pickupDescription ||
                  "Identique à la prise en charge"
                : "Non défini"
            }
            type="dropoff"
          />
        </div>

        <CButton
          className="mt-3"
          style={{
            backgroundColor: colors.yellow,
            border: "none",
            color: "#fff",
          }}
          size="sm"
          onClick={openNewStep}
        >
          <PlusIcon width="16" fill="#fff" /> Ajouter une étape
        </CButton>
      </SectionCard>

      {wizardOpen && (
        <StepWizardModal
          initial={
            editingIndex !== null
              ? toDraft(itineraryFields[editingIndex])
              : emptyDraft()
          }
          onCancel={() => {
            setWizardOpen(false);
            setEditingIndex(null);
          }}
          onSave={handleWizardSave}
        />
      )}
    </div>
  );
}

function TimelineNode({
  icon,
  filled,
  address,
  subtitle,
  actions,
  isLast,
  type,
}: {
  icon?: React.ReactNode;
  filled?: boolean;
  address?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  isLast?: boolean;
  type?: string;
}) {
  return (
    <div
      className="d-flex gap-3 position-relative"
      style={{ zIndex: 1, marginBottom: isLast ? 0 : 20 }}
    >
      <div
        className="d-flex align-items-center justify-content-center shrink-0"
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: filled ? colors.yellow : "#fff",
          border: filled ? "none" : `2px solid ${colors.yellow}`,
          color: filled ? "#fff" : colors.primary,
        }}
      >
        {icon ?? <MapPin size={14} />}
      </div>
      <div className="flex-grow-1 pt-1 d-flex justify-content-between align-items-start">
        <div>
          <div className="fw-semibold" style={{ color: colors.primary }}>
            {address}{" "}
            <span className="text-muted small fw-normal text-capitalize">
              ({type})
            </span>
          </div>
          {subtitle && <div className="small text-muted">{subtitle}</div>}
        </div>
        {actions}
      </div>
    </div>
  );
}

/** Inline row of flexed icon buttons: move up, move down, edit, delete. */
function StepActions({
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  onEdit: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete: () => void;
}) {
  const iconBtnStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    border: "none",
    background: "none",
    borderRadius: 6,
    color: "black",
    cursor: "pointer",
  };

  const disabledStyle: React.CSSProperties = {
    ...iconBtnStyle,
    color: "#ccc",
    cursor: "not-allowed",
  };

  return (
    <div className="d-flex align-items-center gap-1">
      <button
        type="button"
        style={onMoveUp ? iconBtnStyle : disabledStyle}
        onClick={onMoveUp}
        disabled={!onMoveUp}
        aria-label="Monter"
        title="Monter"
      >
        <ArrowUp size={15} />
      </button>
      <button
        type="button"
        style={onMoveDown ? iconBtnStyle : disabledStyle}
        onClick={onMoveDown}
        disabled={!onMoveDown}
        aria-label="Descendre"
        title="Descendre"
      >
        <ArrowDown size={15} />
      </button>
      <button
        type="button"
        style={iconBtnStyle}
        onClick={onEdit}
        aria-label="Modifier"
        title="Modifier"
      >
        <Pencil size={15} />
      </button>
      <button
        type="button"
        style={{ ...iconBtnStyle, color: "#dc3545" }}
        onClick={onDelete}
        aria-label="Retirer"
        title="Retirer"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

function WizardFooter({
  onBack,
  onNext,
  onSkip,
  nextDisabled,
  nextLabel = "Next",
}: {
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
}) {
  return (
    <div className="d-flex justify-content-between align-items-center mt-4">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          style={{ border: "none", background: "none", color: "#0071eb" }}
        >
          ← Retour
        </button>
      ) : (
        <span />
      )}
      <div className="d-flex align-items-center gap-3">
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            style={{ border: "none", background: "none", color: "#0071eb" }}
          >
            Ignorer
          </button>
        )}
        <CButton
          type="button"
          style={{
            backgroundColor: colors.primary,
            border: "none",
            borderRadius: 20,
          }}
          disabled={nextDisabled}
          onClick={onNext}
        >
          {nextLabel}
        </CButton>
      </div>
    </div>
  );
}

/** Chip-tag input for typed free-text activity entries, e.g. "Guided tour ✕  Walk ✕". */
function ActivityChipInput({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draftText, setDraftText] = useState("");

  const commit = () => {
    const v = draftText.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraftText("");
  };

  return (
    <div
      className="d-flex flex-wrap align-items-center gap-2 p-2"
      style={{
        border: "1px solid #ddd",
        borderRadius: 8,
        minHeight: 44,
      }}
    >
      {values.map((v) => (
        <span
          key={v}
          className="d-flex align-items-center gap-1"
          style={{
            backgroundColor: "#f1f1f1",
            borderRadius: 999,
            padding: "0.25rem 0.6rem",
            fontSize: 13,
          }}
        >
          {v}
          <button
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            style={{ border: "none", background: "none", lineHeight: 0 }}
            aria-label={`Retirer ${v}`}
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        value={draftText}
        onChange={(e) => setDraftText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && !draftText && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={values.length ? "" : "Saisir une activité..."}
        style={{
          border: "none",
          outline: "none",
          flex: 1,
          minWidth: 120,
          fontSize: 14,
        }}
      />
    </div>
  );
}

/** Tabbed, multi-select category browser — the "Select from list" panel. */
function ActivityListPicker({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (item: string) => void;
}) {
  const [activeTab, setActiveTab] = useState(
    Object.keys(ACTIVITY_CATEGORIES)[0],
  );
  const groups = ACTIVITY_CATEGORIES[activeTab];

  return (
    <div>
      <div className="d-flex gap-3 border-bottom mb-3 flex-wrap">
        {Object.keys(ACTIVITY_CATEGORIES).map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveTab(cat)}
            style={{
              border: "none",
              background: "none",
              padding: "6px 0",
              fontWeight: activeTab === cat ? 700 : 400,
              color: activeTab === cat ? colors.primary : "#888",
              borderBottom:
                activeTab === cat ? `2px solid ${colors.primary}` : "none",
            }}
          >
            {cat}
          </button>
        ))}
      </div>
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {Object.entries(groups).map(([groupLabel, items]) => (
          <div key={groupLabel || "main"} className="mb-3">
            {groupLabel && (
              <div className="fw-semibold small text-muted mb-2">
                {groupLabel}
              </div>
            )}
            <div className="row g-2">
              {items.map((item) => (
                <div className="col-6" key={item}>
                  <label
                    className="d-flex align-items-center gap-2"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(item)}
                      onChange={() => onToggle(item)}
                    />
                    <span className="small">{item}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepWizardModal({
  initial,
  onCancel,
  onSave,
}: {
  initial: StepDraft;
  onCancel: () => void;
  onSave: (draft: StepDraft) => void;
}) {
  const [stage, setStage] = useState<WizardStage>(
    initial.type
      ? initial.type === "activity"
        ? "activityPick"
        : "transferPick"
      : "type",
  );
  const [draft, setDraft] = useState<StepDraft>(initial);
  const [showListPicker, setShowListPicker] = useState(false);
  const [customTitle, setCustomTitle] = useState(
    Boolean(draft.title) && !draft.address,
  );

  const patch = (p: Partial<StepDraft>) => setDraft((d) => ({ ...d, ...p }));

  const toggleListItem = (item: string) => {
    patch({
      selectedActivities: draft.selectedActivities.includes(item)
        ? draft.selectedActivities.filter((x) => x !== item)
        : [...draft.selectedActivities, item],
    });
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-3 p-4"
        style={{
          width: 560,
          maxWidth: "92vw",
          maxHeight: "88vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex justify-content-end">
          <button
            type="button"
            onClick={onCancel}
            style={{ border: "none", background: "none" }}
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {stage === "type" && (
          <>
            <h5 className="fw-bold mb-4">Que se passe-t-il ensuite ?</h5>
            {(["activity", "transfer"] as const).map((t) => (
              <label
                key={t}
                className="d-flex align-items-center gap-2 mb-3"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  checked={draft.type === t}
                  onChange={() =>
                    patch(
                      t === "transfer"
                        ? {
                            type: t,
                            address: "",
                            placeName: "",
                            placeId: "",
                            latitude: undefined,
                            longitude: undefined,
                            city: "",
                          }
                        : { type: t },
                    )
                  }
                />
                <span>
                  {t === "activity"
                    ? "Une activité (la raison de la réservation)"
                    : "Un transfert (transport entre les temps forts)"}
                </span>
              </label>
            ))}
            <WizardFooter
              onNext={() =>
                setStage(
                  draft.type === "activity" ? "activityPick" : "transferPick",
                )
              }
              nextDisabled={!draft.type}
            />
          </>
        )}

        {stage === "activityPick" && (
          <>
            <h5 className="fw-bold mb-2">
              Que se passe-t-il pendant cette étape ?
            </h5>
            <p className="text-muted small mb-3">
              Saisissez une ou plusieurs activités, ou choisissez-en dans la
              liste. Vous pouvez combiner plusieurs activités pour une même
              étape.
            </p>

            <ActivityChipInput
              values={draft.selectedActivities}
              onChange={(next) => patch({ selectedActivities: next })}
            />

            <button
              type="button"
              onClick={() => setShowListPicker((v) => !v)}
              className="mt-2 mb-3"
              style={{
                border: "none",
                background: "none",
                color: colors.primary,
                fontWeight: 600,
                fontSize: 13,
                padding: 0,
              }}
            >
              {showListPicker
                ? "Masquer la liste ▲"
                : "ou Sélectionner dans la liste ▼"}
            </button>

            {showListPicker && (
              <div
                className="p-3 mb-3"
                style={{ border: "1px solid #eee", borderRadius: 10 }}
              >
                <ActivityListPicker
                  selected={draft.selectedActivities}
                  onToggle={toggleListItem}
                />
              </div>
            )}

            <WizardFooter
              onBack={() => setStage("type")}
              onNext={() => setStage("location")}
              nextDisabled={draft.selectedActivities.length === 0}
            />
          </>
        )}

        {stage === "transferPick" && (
          <>
            <h5 className="fw-bold mb-2">Comment se déroule ce transfert ?</h5>
            <p className="text-muted small mb-3">
              Ex : Van, Minibus, À pied...
            </p>
            <CFormInput
              className="mb-3"
              placeholder="Ex: Van"
              value={draft.transferMode}
              onChange={(e) => patch({ transferMode: e.target.value })}
            />
            <WizardFooter
              onBack={() => setStage("type")}
              onNext={() => setStage("duration")}
              nextDisabled={!draft.transferMode}
            />
          </>
        )}

        {stage === "location" && (
          <>
            <h5 className="fw-bold mb-2">Où cela se passe-t-il ?</h5>
            <p className="text-muted small mb-3">
              {customTitle
                ? "Saisissez un titre personnalisé pour cette étape."
                : "Recherchez un lieu ou cliquez sur la carte. Vous pouvez ignorer cette étape si l'emplacement n'est pas pertinent."}
            </p>

            {customTitle ? (
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Titre de l'étape"
                value={draft.title ?? ""}
                onChange={(e) => patch({ title: e.target.value })}
                autoFocus
              />
            ) : (
              <LocationPicker
                value={
                  draft.latitude && draft.longitude
                    ? {
                        latitude: draft.latitude,
                        longitude: draft.longitude,
                        address: draft.address,
                        placeId: draft.placeId,
                        name: draft.placeName,
                      }
                    : undefined
                }
                onChange={(loc) =>
                  patch({
                    latitude: loc.latitude,
                    longitude: loc.longitude,
                    address: loc.address,
                    placeId: loc.placeId,
                    placeName: loc.name,
                    city: "",
                  })
                }
              />
            )}

            <button
              type="button"
              className="btn btn-link p-0 mb-3 text-decoration-none"
              onClick={() => {
                if (!customTitle) {
                  // switching to title only: drop any location
                  patch({
                    city: "",
                    placeId: "",
                    address: "",
                    placeName: "",
                    latitude: undefined,
                    longitude: undefined,
                  });
                }
                setCustomTitle((v) => !v);
              }}
            >
              {customTitle ? "Utiliser une adresse" : "Personnaliser"}
            </button>

            <WizardFooter
              onBack={() =>
                setStage(
                  draft.type === "activity" ? "activityPick" : "transferPick",
                )
              }
              onNext={() => setStage("duration")}
              nextDisabled={customTitle && !draft.title?.trim()}
              onSkip={() => {
                patch({
                  city: "",
                  placeId: "",
                  address: "",
                  placeName: "",
                  latitude: undefined,
                  longitude: undefined,
                });
                setStage("duration");
              }}
            />
          </>
        )}

        {stage === "duration" && (
          <>
            <h5 className="fw-bold mb-2">
              Quelle est la durée de cette étape ?
            </h5>
            <p className="text-muted small mb-3">
              Vous pouvez ignorer cette étape si vous ne souhaitez pas afficher
              de durée.
            </p>
            <div className="d-flex align-items-center gap-3 mb-3">
              <CFormInput
                type="number"
                min={0}
                style={{ width: 100 }}
                value={draft.durationHours}
                onChange={(e) => patch({ durationHours: e.target.value })}
              />
              <span>Heures</span>
              <CFormInput
                type="number"
                min={0}
                style={{ width: 100 }}
                value={draft.durationMinutes}
                onChange={(e) => patch({ durationMinutes: e.target.value })}
              />
              <span>Minutes</span>
            </div>
            <WizardFooter
              onBack={() =>
                setStage(
                  draft.type === "activity" ? "location" : "transferPick",
                )
              }
              onNext={() => setStage("optional")}
              onSkip={() => {
                patch({ durationHours: "", durationMinutes: "" });
                setStage("optional");
              }}
            />
          </>
        )}

        {stage === "optional" && (
          <>
            <h5 className="fw-bold mb-4">
              Cette partie est-elle facultative ?
            </h5>
            {[
              { label: "Oui", value: true },
              { label: "Non", value: false },
            ].map((opt) => (
              <label
                key={opt.label}
                className="d-flex align-items-center gap-2 mb-3"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  checked={draft.isOptional === opt.value}
                  onChange={() => patch({ isOptional: opt.value })}
                />
                <span>{opt.label}</span>
              </label>
            ))}
            <WizardFooter
              onBack={() => setStage("duration")}
              onNext={() => onSave(draft)}
              nextLabel="Enregistrer"
            />
          </>
        )}
      </div>
    </div>
  );
}
